import type {
  ChartModel,
  DeviceLine,
} from '../../SensorTimeline/lib/chartModel';
import { DEVICE_COLORS, type Device } from '../../SensorTimeline/lib/devices';
import { extentOf, type Extent } from '../../SensorTimeline/lib/extent';
import type { Segment } from '../../SensorTimeline/lib/gaps';
import {
  estimateTextWidth,
  type MeasureText,
  type XTick,
  type XTickAnchor,
} from '../../SensorTimeline/lib/layout';
import { CHART_MEASURES } from '../../SensorTimeline/lib/measures';
import type { GxpExcursionEvent } from './types';

/** One sensor as the temperature-series endpoint returns it; timestamps are ISO-8601 UTC. */
export interface ChartSensor {
  sensor_tracking_id: string;
  display_label?: string;
  lower_bound_c?: number;
  upper_bound_c?: number;
  series?: { ts: string; value: number | boolean | null }[];
  gaps?: { from: string; to: string }[];
}

/** The temperature-series endpoint's response. */
export interface TemperatureSeries {
  has_data?: boolean;
  pop_utc?: string | null;
  pod_utc?: string | null;
  window_start_utc?: string | null;
  window_end_utc?: string | null;
  sensors?: ChartSensor[];
}

/** One excursion's highlighted interval and its peak pin, at the excursion's start when no reading fell inside it (as the PDF). */
export interface ExcursionMark {
  key: string;
  from: number;
  to: number;
  peakAt: number | null;
  peak: number | null;
  below: boolean;
}

/** Everything the chart draws, in true epoch ms. */
export interface TemperatureProfile {
  devices: Device[];
  model: ChartModel;
  window: Extent;
  pop: number | null;
  pod: number | null;
  limits: Extent | null;
  sensorLimits: ReadonlyMap<string, Extent>;
  gaps: Extent[];
  excursions: ExcursionMark[];
}

const Y_MARGIN_C = 2;
const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const TICK_STEPS_MIN = [
  15, 30, 60, 120, 180, 360, 720, 1440, 2880, 4320, 10080,
];

/** Room each hours-since-POP label needs; wider than the vitals axis so steps stay coarse (Figma: 0h, 12h … 84h). */
export const ELAPSED_TICK_SPACING_PX = 120;

const parseUtc = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
};

/** Index runs between the backend's "no data" gaps, so the line breaks exactly where a gap band is drawn. */
const segmentsBetween = (
  t: readonly number[],
  gapStarts: ReadonlySet<number>
): Segment[] => {
  const segments: Segment[] = [];
  let start = 0;
  for (let i = 1; i < t.length; i += 1) {
    if (gapStarts.has(t[i - 1])) {
      segments.push([start, i - 1]);
      start = i;
    }
  }
  if (t.length > 0) segments.push([start, t.length - 1]);
  return segments;
};

const sensorLine = (sensor: ChartSensor, device: Device): DeviceLine => {
  const t: number[] = [];
  const v: number[] = [];
  for (const point of sensor.series ?? []) {
    const at = parseUtc(point.ts);
    if (at === null || typeof point.value !== 'number') continue;
    t.push(at);
    v.push(point.value);
  }
  const gapStarts = new Set(
    (sensor.gaps ?? []).flatMap((gap) => {
      const from = parseUtc(gap.from);
      return from === null ? [] : [from];
    })
  );
  return { device, t, v, segments: segmentsBetween(t, gapStarts) };
};

const limitsOf = (sensor: ChartSensor | undefined): Extent | null =>
  sensor?.lower_bound_c != null && sensor.upper_bound_c != null
    ? [sensor.lower_bound_c, sensor.upper_bound_c]
    : null;

const valueDomain = (
  lines: readonly DeviceLine[],
  limits: Extent | null
): Extent => {
  const extent = extentOf([...lines.map((line) => line.v), limits ?? []]);
  if (!extent) return [0, 1];
  return [
    Math.floor(extent[0] - Y_MARGIN_C),
    Math.ceil(extent[1] + Y_MARGIN_C),
  ];
};

/** The chart's lines, window, POP/POD, limits, gaps and excursion marks from the series and the report's excursions. */
export const temperatureProfile = (
  series: TemperatureSeries | null | undefined,
  events: readonly GxpExcursionEvent[]
): TemperatureProfile => {
  const sensors = series?.sensors ?? [];
  const devices: Device[] = sensors.map((sensor, index) => ({
    id: sensor.sensor_tracking_id,
    label: sensor.display_label || sensor.sensor_tracking_id,
    color: DEVICE_COLORS[index % DEVICE_COLORS.length],
  }));
  const allLines = sensors.map((sensor, index) =>
    sensorLine(sensor, devices[index])
  );
  const lines = allLines.filter((line) => line.t.length > 0);
  const limits = limitsOf(sensors[0]);
  const sensorLimits = new Map(
    sensors.flatMap((sensor) => {
      const own = limitsOf(sensor);
      return own ? [[sensor.sensor_tracking_id, own] as const] : [];
    })
  );

  const times = lines.flatMap((line) => [line.t[0], line.t[line.t.length - 1]]);
  let start = parseUtc(series?.window_start_utc) ?? Math.min(...times);
  let end = parseUtc(series?.window_end_utc) ?? Math.max(...times);
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    start = 0;
    end = HOUR_MS;
  } else if (start >= end) {
    start -= HOUR_MS;
    end += HOUR_MS;
  }

  return {
    devices,
    model: {
      measure: CHART_MEASURES.temperature,
      lines,
      domain: valueDomain(lines, limits),
      missing: allLines
        .filter((line) => line.t.length === 0)
        .map((line) => line.device),
    },
    window: [start, end],
    pop: parseUtc(series?.pop_utc),
    pod: parseUtc(series?.pod_utc),
    limits,
    sensorLimits,
    gaps: sensors.flatMap((sensor) =>
      (sensor.gaps ?? []).flatMap((gap) => {
        const from = parseUtc(gap.from);
        const to = parseUtc(gap.to);
        return from === null || to === null ? [] : [[from, to] as const];
      })
    ),
    excursions: events.flatMap((event) => {
      const from = parseUtc(event.start_utc);
      const to = parseUtc(event.end_utc);
      if (from === null || to === null) return [];
      return [
        {
          key: `${event.sensor_tracking_id ?? ''}-${event.event_id ?? ''}`,
          from,
          to,
          peakAt: parseUtc(event.peak_utc) ?? from,
          peak: typeof event.peak === 'number' ? event.peak : null,
          below: event.side === 'below',
        },
      ];
    }),
  };
};

/** Minutes since POP as the axis prints them: "12h", "1h 30m" or "45m". */
export const elapsedLabel = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (rest === 0) return `${hours}h`;
  return hours ? `${hours}h ${rest}m` : `${rest}m`;
};

/**
 * X-axis labels in hours since POP (Figma), on the finest round step that fits across `plotWidth`, from 0h onward.
 * A label centred on its tick that would overhang either end of the plot is anchored to that end instead.
 */
export const elapsedTicks = (
  origin: number,
  window: Extent,
  plotWidth: number,
  measure: MeasureText = estimateTextWidth
): XTick[] => {
  const maxTicks = Math.max(2, Math.floor(plotWidth / ELAPSED_TICK_SPACING_PX));
  const first = Math.max(window[0], origin);
  const span = window[1] - first;
  const step =
    TICK_STEPS_MIN.find(
      (minutes) => Math.floor(span / (minutes * MINUTE_MS)) + 1 <= maxTicks
    ) ?? TICK_STEPS_MIN[TICK_STEPS_MIN.length - 1];
  const stepMs = step * MINUTE_MS;
  const windowSpan = window[1] - window[0];
  const ticks: XTick[] = [];
  for (
    let k = Math.ceil((first - origin) / stepMs);
    origin + k * stepMs <= window[1];
    k += 1
  ) {
    const at = origin + k * stepMs;
    const label = elapsedLabel(k * step);
    const x = windowSpan > 0 ? ((at - window[0]) / windowSpan) * plotWidth : 0;
    const half = measure(label) / 2;
    const anchor: XTickAnchor =
      x < half ? 'start' : x > plotWidth - half ? 'end' : 'middle';
    ticks.push({ at, label, anchor });
  }
  return ticks;
};
